import { useState, useEffect } from "react";
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks } from "date-fns";
import { de, enUS, pt } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, XCircle, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import ShiftModal from "@/components/ShiftModal";
import AbsenceModal from "@/components/AbsenceModal";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WeeklyViewPage({ onLogout }) {
  const { lang, t } = useLanguage();
  const dateLocale = lang === "pt" ? pt : lang === "en" ? enUS : de;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [absenceModalOpen, setAbsenceModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedAbsence, setSelectedAbsence] = useState(null);

  const weekStart = startOfWeek(currentDate, { locale: dateLocale, weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { locale: dateLocale, weekStartsOn: 1 });
  const daysInWeek = eachDayOfInterval({ start: weekStart, end: weekEnd });

  useEffect(() => {
    loadData();
  }, [currentDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const monthStr = format(currentDate, 'yyyy-MM');

      const [shiftsRes, absencesRes, workersRes, shiftTypesRes] = await Promise.all([
        axios.get(`${API}/shifts?month=${monthStr}`, { headers }),
        axios.get(`${API}/absences?month=${monthStr}`, { headers }),
        axios.get(`${API}/workers`, { headers }),
        axios.get(`${API}/shift-types`, { headers })
      ]);

      setShifts(shiftsRes.data);
      setAbsences(absencesRes.data);
      setWorkers(workersRes.data);
      setShiftTypes(shiftTypesRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handlePrevWeek = () => setCurrentDate(subWeeks(currentDate, 1));
  const handleNextWeek = () => setCurrentDate(addWeeks(currentDate, 1));

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const res = await axios.get(`${API}/reports/weekly-pdf?week_start=${weekStartStr}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob', timeout: 30000
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `Wochenplan_KW${format(weekStart, 'ww')}_${weekStartStr}.pdf`);
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch (err) {
      console.error("Weekly PDF export failed:", err);
      toast.error("PDF-Export fehlgeschlagen");
    }
  };

  const handleAddShift = (day, workerId) => {
    setSelectedDate(format(day, 'yyyy-MM-dd'));
    setSelectedShift(workerId ? { worker_id: workerId } : null);
    setShiftModalOpen(true);
  };

  const handleAddAbsence = (day, workerId) => {
    setSelectedDate(format(day, 'yyyy-MM-dd'));
    setSelectedAbsence(workerId ? { worker_id: workerId } : null);
    setAbsenceModalOpen(true);
  };

  const getShiftsForWorkerAndDay = (workerId, day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return shifts.filter(s => s.worker_id === workerId && s.date === dateStr);
  };

  const getAbsencesForWorkerAndDay = (workerId, day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return absences.filter(a => a.worker_id === workerId && a.date === dateStr);
  };

  const getShiftTime = (shift) => {
    if (shift.custom_start_time && shift.custom_end_time) {
      return `${shift.custom_start_time} - ${shift.custom_end_time}`;
    }
    const type = shiftTypes.find(st => st.id === shift.shift_type_id);
    if (!type) return '';
    return `${type.start_time} - ${type.end_time}`;
  };

  const calculateShiftHours = (shift) => {
    let startTime, endTime;
    
    if (shift.custom_start_time && shift.custom_end_time) {
      startTime = shift.custom_start_time;
      endTime = shift.custom_end_time;
    } else {
      const type = shiftTypes.find(st => st.id === shift.shift_type_id);
      if (!type || type.start_time === '--:--' || type.end_time === '--:--') return null;
      startTime = type.start_time;
      endTime = type.end_time;
    }
    
    if (startTime === '--:--' || endTime === '--:--') return null;
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    let hours = endHour - startHour;
    let minutes = endMin - startMin;
    
    if (minutes < 0) {
      hours -= 1;
      minutes += 60;
    }
    
    if (hours < 0) hours += 24;
    
    const totalHours = hours + (minutes / 60);
    return totalHours % 1 === 0 ? `${totalHours}h` : `${totalHours.toFixed(1)}h`;
  };

  const getShiftTypeColor = (typeId) => {
    const type = shiftTypes.find(st => st.id === typeId);
    return type?.color || 'gray';
  };

  const colorClasses = {
    orange: 'bg-orange-100 text-orange-800 border-orange-300',
    blue: 'bg-blue-100 text-blue-800 border-blue-300',
    indigo: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    gray: 'bg-gray-100 text-gray-800 border-gray-300',
    green: 'bg-green-100 text-green-800 border-green-300',
    purple: 'bg-purple-100 text-purple-800 border-purple-300',
    red: 'bg-red-100 text-red-800 border-red-300',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    pink: 'bg-pink-100 text-pink-800 border-pink-300',
    teal: 'bg-teal-100 text-teal-800 border-teal-300'
  };

  const absenceColorClasses = {
    'Urlaub': 'bg-green-100 text-green-800 border-green-300',
    'Krank': 'bg-red-100 text-red-800 border-red-300',
    'Schule': 'bg-purple-100 text-purple-800 border-purple-300'
  };

  if (loading) {
    return (
      <Layout onLogout={onLogout}>
        <div className="flex items-center justify-center h-full">
          <p className="text-slate-600">Laden...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout onLogout={onLogout}>
      <div className="p-6" data-testid="weekly-view-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("weekly_title")}
            </h1>
            <p className="text-slate-600 mt-1">Detaillierte Wochenplanung</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <Button variant="ghost" size="sm" onClick={handlePrevWeek}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {format(weekStart, 'dd.MM.yyyy', { locale: dateLocale })} - {format(weekEnd, 'dd.MM.yyyy', { locale: dateLocale })}
              </h2>
              <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="weekly-pdf-btn">
                <FileDown className="w-4 h-4 mr-1.5" /> PDF
              </Button>
            </div>
            
            <Button variant="ghost" size="sm" onClick={handleNextWeek}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full min-w-max">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 w-40 sticky left-0 bg-slate-50 z-10 border-r border-slate-200">Mitarbeiter</th>
                  {daysInWeek.map(day => (
                    <th key={day.toString()} className="px-3 py-3 text-center text-sm font-semibold text-slate-700 min-w-[140px]">
                      <div>{format(day, 'EEE', { locale: dateLocale })}</div>
                      <div className="text-xs text-slate-500">{format(day, 'dd.MM')}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {workers.map(worker => (
                  <tr key={worker.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900 sticky left-0 bg-white z-10 border-r border-slate-200">
                      <div className="truncate max-w-[150px]">{worker.name}</div>
                      {worker.role && <div className="text-xs text-slate-500 truncate max-w-[150px]">{worker.role}</div>}
                    </td>
                    {daysInWeek.map(day => {
                      const dayShifts = getShiftsForWorkerAndDay(worker.id, day);
                      const dayAbsences = getAbsencesForWorkerAndDay(worker.id, day);
                      
                      return (
                        <td key={day.toString()} className="px-3 py-2 border-l border-slate-100">
                          <div className="space-y-1 min-h-[60px]">
                            {dayShifts.map(shift => {
                              const color = getShiftTypeColor(shift.shift_type_id);
                              const hasCustomTime = !!(shift.custom_start_time && shift.custom_end_time);
                              const shiftHours = calculateShiftHours(shift);
                              return (
                                <div
                                  key={shift.id}
                                  className={`rounded px-2 py-1.5 text-xs font-medium border cursor-pointer hover:brightness-95 ${colorClasses[color]} ${hasCustomTime ? 'ring-1 ring-blue-400' : ''}`}
                                  onClick={() => {
                                    setSelectedShift(shift);
                                    setSelectedDate(shift.date);
                                    setShiftModalOpen(true);
                                  }}
                                >
                                  <div className="font-semibold text-[10px]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                    {getShiftTime(shift)}
                                  </div>
                                  {shiftHours && (
                                    <div className="font-bold text-[10px] opacity-90" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                      {shiftHours}
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            {dayAbsences.map(absence => (
                              <div
                                key={absence.id}
                                className={`rounded px-2 py-1.5 text-xs font-medium border-2 border-dashed cursor-pointer hover:brightness-95 ${absenceColorClasses[absence.type]}`}
                                onClick={() => {
                                  setSelectedAbsence(absence);
                                  setSelectedDate(absence.date);
                                  setAbsenceModalOpen(true);
                                }}
                              >
                                <div className="flex items-center gap-1">
                                  <XCircle className="w-3 h-3" />
                                  <span className="text-[10px]">{absence.type}</span>
                                </div>
                              </div>
                            ))}

                            {dayShifts.length === 0 && dayAbsences.length === 0 && (
                              <div className="flex items-center justify-center h-[60px]">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button className="w-6 h-6 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600">
                                      <Plus className="w-4 h-4" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    <DropdownMenuItem onClick={() => handleAddShift(day, worker.id)}>
                                      Schicht hinzufügen
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleAddAbsence(day, worker.id)}>
                                      Abwesenheit hinzufügen
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {shiftModalOpen && (
        <ShiftModal
          open={shiftModalOpen}
          onClose={() => {
            setShiftModalOpen(false);
            setSelectedShift(null);
            setSelectedDate(null);
          }}
          date={selectedDate}
          shift={selectedShift}
          workers={workers}
          shiftTypes={shiftTypes}
          onSuccess={loadData}
        />
      )}

      {absenceModalOpen && (
        <AbsenceModal
          open={absenceModalOpen}
          onClose={() => {
            setAbsenceModalOpen(false);
            setSelectedAbsence(null);
            setSelectedDate(null);
          }}
          date={selectedDate}
          absence={selectedAbsence}
          workers={workers}
          onSuccess={loadData}
        />
      )}
    </Layout>
  );
}